import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { RadioOptionsWithConfirm, IQuestion, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import AdditionalPassports from './additionalPassports';
import { IAdditionalPassport } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { useNavigate } from 'react-router-dom';

const VisaPrepFormAddPIPage1 = (_props: any) => {
  // dispatch actions
  const dispatch = useDispatch();

  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE1));
  };

  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const hasAdditionalPassports = useSelector(
    ({ student }: ApplicationState) => student.visa.hasAdditionalPassports,
    shallowEqual,
  );

  const defaultArr = useMemo(() => {
    return [];
  }, []);
  const additionalPassports =
    useSelector(({ student }: ApplicationState) => student.visa.additionalPassports, shallowEqual) || defaultArr;

  // constants
  const currentStep = 1;
  const steps = 2;

  // component state
  const [complete, setComplete] = useState(false);
  const [additionalPassportsState, setAdditionalPassportsState] = useState<IAdditionalPassport[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setAdditionalPassportsState([
      ...additionalPassports,
      {
        docNumber: '',
        placeOfIssue: '',
        issuingAuthority: '',
        issueDate: '',
        expiryDate: '',
      },
    ]);
    setShowModal([...additionalPassports.map(_ => false), false]);
  }, [additionalPassports]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasAdditionalPassports,
      additionalPassports,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, hasAdditionalPassports, additionalPassports]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deletePassport = (index: number): void => {
    onChange(VQ.ADDITIONAL_PASSPORTS, [
      ...additionalPassports.slice(0, index),
      ...additionalPassports.slice(index + 1),
    ]);
  };

  const onHasAdditionalClick = (question: IQuestion) => {
    onChange(VQ.HAS_ADDITIONAL_PASSPORTS, question.label === 'Yes');
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Additional Personal Information'}
    >
      <RadioOptionsWithConfirm
        key={VQ.HAS_ADDITIONAL_PASSPORTS}
        id={VQ.HAS_ADDITIONAL_PASSPORTS}
        label={'Have you had any other passports in the last 10 years?'}
        subText={'(other than the ones in the document upload section)'}
        onClick={(question: any) => onHasAdditionalClick(question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasAdditionalPassports === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasAdditionalPassports === false,
          },
        ]}
      />

      {hasAdditionalPassports === true && (
        <CardContainer title={`Additional Passports`}>
          {additionalPassportsState.map((passport: IAdditionalPassport, index: number) => (
            <CardWithModal
              key={index}
              id={`additional-passport-${index}`}
              index={index}
              modalTitle={'Add an Additional Passport'}
              cardItems={[
                {
                  label: 'Document number',
                  value: passport.docNumber,
                },
                {
                  label: 'Place of issue',
                  value: passport.placeOfIssue,
                },
                {
                  label: 'Issuing authority',
                  value: passport.issuingAuthority,
                },
                {
                  label: 'Date of issue',
                  value: passport.issueDate,
                },
                {
                  label: 'Date of expiry',
                  value: passport.expiryDate,
                },
              ]}
              onRemove={deletePassport}
              addButtonLabel={`Add Additional Passport`}
              addAnotherButtonLabel={`I have another passport to add`}
              hasAddMoreButton={
                passport.docNumber === '' &&
                passport.placeOfIssue === '' &&
                passport.issuingAuthority === '' &&
                passport.issueDate === '' &&
                passport.expiryDate === ''
              }
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <AdditionalPassports
                index={index}
                docNumber={passport.docNumber}
                placeOfIssue={passport.placeOfIssue}
                issuingAuthority={passport.issuingAuthority}
                issueDate={passport.issueDate}
                expiryDate={passport.expiryDate}
                onSaveAndClose={(passport: IAdditionalPassport) => {
                  const passports = [...additionalPassports];
                  passports[index] = {
                    ...passport,
                  };
                  onChange(VQ.ADDITIONAL_PASSPORTS, passports);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}
    </QuestionPage>
  );
};
export default VisaPrepFormAddPIPage1;
